import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Chip, List, ListItemButton, ListItemIcon, ListItemText,
  Paper, Skeleton, Stack, Tab, Tabs, useMediaQuery,
} from '@mui/material';
import {
  BsArrowLeft, BsBoxSeam, BsBuilding, BsClipboardData,
  BsExclamationTriangle, BsFileEarmarkText, BsJournalText, BsListCheck,
  BsPaperclip, BsPeople,
} from 'react-icons/bs';

import axiosInstance from '../../../../axiosInstance';
import CoquilleExpert from '../../../../components/Expert/CoquilleExpert';
import { themeExpert } from '../../../../theme/expert';

import EditExpertise from '../../../../components/Expmanage/editExpertise';
import EditContract from '../../../../components/Contract/editContract';
import EditClaims from '../../../../components/Claims/editClaims';
import Report from '../../../../components/Report';
import Contents from '../../../../components/Contents';
import Costings from '../../../../components/Costings';
import Files from '../../../../components/Files';
import Contact from '../../../../components/Contact';
import Tasks from '../../../../components/Tasks';

/**
 * Les sections du dossier, dans l'ordre où on les remplit.
 *
 * Les anciennes icônes étaient quatre images pour neuf onglets — « Expertise »
 * et « Contrat » portaient la même, « Bâtiment » et « Contenu » aussi. Une
 * icône qui ne distingue rien ne sert qu'à occuper la place : chaque section
 * a maintenant la sienne.
 */
const SECTIONS = [
  { cle: 'expertise', libelle: 'Expertise', Icone: BsClipboardData,
    Composant: EditExpertise },
  { cle: 'suivi', libelle: 'Suivi', Icone: BsListCheck, Composant: Tasks },
  { cle: 'contrat', libelle: 'Contrat', Icone: BsFileEarmarkText,
    Composant: EditContract },
  { cle: 'sinistre', libelle: 'Sinistre', Icone: BsExclamationTriangle,
    Composant: EditClaims },
  { cle: 'intervenants', libelle: 'Intervenants', Icone: BsPeople,
    Composant: Contact },
  { cle: 'batiment', libelle: 'Bâtiment', Icone: BsBuilding,
    Composant: Costings },
  { cle: 'contenu', libelle: 'Contenu', Icone: BsBoxSeam,
    Composant: Contents },
  { cle: 'documents', libelle: 'Documents', Icone: BsPaperclip,
    Composant: Files },
  { cle: 'rapport', libelle: 'Rapport', Icone: BsJournalText,
    Composant: Report },
];

const STATUTS = {
  'Préparation': 'warning',
  'Contradictoire': 'info',
  'Clôture': 'success',
  'Annuler': 'default',
};

function ManagerExpertise() {
  const { expertiseId } = useParams();
  // `?onglet=rapport` : la recherche ouvre le dossier là où elle a trouvé.
  const [params] = useSearchParams();
  const [section, setSection] = useState(
    SECTIONS.some((s) => s.cle === params.get('onglet'))
      ? params.get('onglet') : SECTIONS[0].cle);
  const [dossier, setDossier] = useState(null);
  const compact = useMediaQuery(themeExpert.breakpoints.down('md'),
                                { noSsr: true });

  // L'écran ne disait jamais dans quel dossier on travaillait : neuf onglets,
  // et rien pour le nommer.
  const relire = useCallback(() => {
    axiosInstance.get(`/expertise/${expertiseId}/`)
      .then(({ data }) => setDossier(data))
      .catch(() => setDossier(null));
  }, [expertiseId]);

  useEffect(() => { relire(); }, [relire]);

  const active = SECTIONS.find((s) => s.cle === section) || SECTIONS[0];
  const { Composant } = active;

  const changer = (cle) => {
    setSection(cle);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigation = compact ? (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Tabs value={section} onChange={(_, cle) => changer(cle)}
            variant="scrollable" scrollButtons="auto">
        {SECTIONS.map(({ cle, libelle, Icone }) => (
          <Tab key={cle} value={cle} label={libelle} icon={<Icone />}
               iconPosition="start" sx={{ minHeight: 48 }} />
        ))}
      </Tabs>
    </Paper>
  ) : (
    // Collante, et non fixée : une colonne fixée gardait une hauteur de 50vh
    // en dur et coupait ses derniers onglets sur un écran court.
    <Paper variant="outlined"
           sx={{ position: 'sticky', top: 84, width: 220, flexShrink: 0 }}>
      <List dense disablePadding>
        {SECTIONS.map(({ cle, libelle, Icone }) => (
          <ListItemButton key={cle} selected={cle === section}
                          onClick={() => changer(cle)}>
            <ListItemIcon sx={{ minWidth: 36 }}><Icone /></ListItemIcon>
            <ListItemText primary={libelle} />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );

  return (
    <CoquilleExpert
      titre={dossier ? dossier.name : <Skeleton width={260} />}
      sous_titre={dossier
        ? [dossier.reference, dossier.codeName].filter(Boolean).join(' · ')
        : ' '}
      actions={(
        <Stack direction="row" spacing={1.5} alignItems="center">
          {dossier?.statut && (
            <Chip size="small" label={dossier.statut}
                  color={STATUTS[dossier.statut] || 'default'} />
          )}
          <Button component={Link} to="/expertise" size="small"
                  startIcon={<BsArrowLeft />}>
            Toutes les expertises
          </Button>
        </Stack>
      )}
    >
      <Box sx={{ display: 'flex', gap: 3,
                 flexDirection: { xs: 'column', md: 'row' } }}>
        {navigation}
        {/* Seule la section ouverte est montée : les monter toutes
            déclencherait neuf chargements à l'ouverture du dossier. */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {/* Renommer le dossier doit se voir dans l'en-tête tout de suite ;
              les sections qui n'y touchent pas ignorent la fonction. */}
          <Composant onDossierModifie={relire} />
        </Box>
      </Box>
    </CoquilleExpert>
  );
}

export default ManagerExpertise;
